import { create } from '@bufbuild/protobuf'
import { type Timestamp } from '@bufbuild/protobuf/wkt'
import { type ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { type LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { type SubmitterMasterListItem as ProtoSubmitterMasterListItem } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import { listSubmitterMasters } from '@ebalance/proto/bffuser/v1/occto_master_service-OcctoMasterService_connectquery'
import {
  ListSubmitterMastersRequestSchema,
  type ListSubmitterMastersResponse,
} from '@ebalance/proto/bffuser/v1/occto_master_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import {
  type CivilDate,
  protoDateToCivilDate,
  requireCivilDate,
} from '~/models/civilDate'

/**
 * 計画提出者マスタ 一覧表示行型。
 */
export type SubmitterMasterRow = {
  id: string
  submitterCode: string
  submitterName: string
  areaCode: AreaCode
  licenseType: LicenseType
  memberCount: number
  // AC-10 (occto-master-audit-fix-bundle.md): 紐づく事業者の OCCTO 4 桁コード。
  businessCode: string
  // 紐づく事業者の名称。
  businessName: string
  updatedAt: Timestamp | undefined // optimistic lock 用に proto Timestamp で保持。表示時のみ timestampDate で Date 変換
  updatedBy: string | undefined
  /** 適用開始日 (必須)。 */
  validFrom: CivilDate
  validTo: CivilDate | undefined
}

function convertItem(item: ProtoSubmitterMasterListItem): SubmitterMasterRow {
  return {
    id: item.id,
    submitterCode: item.submitterCode,
    submitterName: item.submitterName,
    areaCode: item.areaCode,
    licenseType: item.licenseType,
    memberCount: item.memberCount,
    businessCode: item.businessCode,
    businessName: item.businessName,
    updatedAt: item.updatedAt,
    updatedBy: item.updatedBy,
    validFrom: requireCivilDate(
      protoDateToCivilDate(item.validFrom),
      `SubmitterMasterListItem.validFrom (id=${item.id})`,
    ),
    validTo: protoDateToCivilDate(item.validTo),
  }
}

export function convertListSubmitterMastersResponse(
  resp: ListSubmitterMastersResponse,
): SubmitterMasterRow[] {
  return resp.items.map(convertItem)
}

/**
 * SubmitterMasterListItem[] (= 集約 RPC レスポンスの field 型) を SubmitterMasterRow[] に変換する。
 * 集約 RPC (GetGenerationSalesPlanMasterFormOptions 等) は SubmitterMasterListItem[] を
 * 直接フィールドとして返すため、convertListSubmitterMastersResponse の代わりに使う。
 */
export function convertSubmitterMasterListItems(
  items: ProtoSubmitterMasterListItem[],
): SubmitterMasterRow[] {
  return items.map(convertItem)
}

export function useListSubmitterMasters(): UseQueryResult<
  ListSubmitterMastersResponse,
  ConnectError
> {
  return useQuery(
    listSubmitterMasters,
    create(ListSubmitterMastersRequestSchema, {}),
  )
}
